import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { RevxMeasure } from 'components/RevxMeasure';
import { MeasureType } from 'domains/measurementDomain';
import { useDebounceFn, useMemoizedFn } from 'ahooks';
import { Radio, Tag } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import { Material } from 'domains/matDomain';
import { useUser } from 'hooks/useUser';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  searchValue?: string;
};

const MatAdminTable = ({ searchValue }: Props) => {
  const { columnTitle, basisWeight, basisWeightTitle } =
    useMeasurementUnit();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  // table data processing
  const data = useAppSelector((state) => state?.mat?.data?.content || []);
  const total = useAppSelector((state) => state.mat.data?.totalElements || 0);
  const currentPage = useAppSelector((state) => state.mat.currentPage);
  const pageSize = useAppSelector((state) => state.mat.pageSize);
  const loading = useAppSelector((state) => state.loading.effects.mat.getList);
  const curItem = useAppSelector((state) => state.mat.item);
  const filterObj = useAppSelector((state) => state.mat.filterObj);

  const [defaultChooseMyPaper, setDefaultChooseMyPaper] = useState(false);
  const [defaultCollapseAdvancedSearch, setDefaultCollapseAdvancedSearch] = useState(false);

  useEffect(() => {
    dispatch.mat.getList({
      page: { currentPage, pageSize },
      query: { ...filterObj },
    });
  }, [currentPage, pageSize, dispatch.mat, searchValue, filterObj]);

  const user = useUser();
  const orgList = useAppSelector((state) => state.org.list);

  const curOrg = useMemo(() => {
    const curOrgId = localStorage.getItem('org') || user?.orgId;
    return orgList.find((org) => org.id === Number(curOrgId));
  }, [orgList, user]);

  useEffect(() => {
    setDefaultChooseMyPaper(Boolean(curOrg?.defaultChooseMyPaper));
    setDefaultCollapseAdvancedSearch(Boolean(curOrg?.defaultCollapseAdvancedSearch));
  }, [curOrg]);

  const onTableChange = useCallback(
    (pagination, filters, sorter, extra) => {
      // console.log(pagination, filters, sorter, extra);
      switch (extra.action) {
        case 'paginate':
          const currentPage = pagination.current;
          const pageSize = pagination.pageSize;
          dispatch.mat.setState({ currentPage, pageSize });
          break;
      }
    },
    [dispatch.mat]
  );

  const columns: TableColumnsType<Material> = [
    {
      title: t('common.id'),
      dataIndex: 'id',
    },
    {
      title: t('platform.mat.form.itemLabels.isPublic'),
      dataIndex: 'isPublic',
      render: (isPublic) => (
        <Tag color={isPublic ? 'green' : 'volcano'}>
          {isPublic
            ? t('platform.mat.table.columns.public')
            : t('platform.mat.table.columns.private')}
        </Tag>
      ),
    },
    {
      title: t('platform.mat.table.columns.manufacturer'),
      dataIndex: 'manufacturerName',
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
    },
    {
      title: t('platform.mat.table.columns.markupPct'),
      dataIndex: 'markupPct',
      valueType: 'percentage',
      responsive: ['xl'],
    },
    {
      title: t('platform.mat.table.columns.stockType'),
      dataIndex: 'stockType',
      responsive: ['xl'],
    },
    {
      title: columnTitle(t('common.size'), MeasureType.LENGTH),
      responsive: ['xl'],
      children: [
        {
          title: t('common.width'),
          dataIndex: 'widthIn',
          render: (value: number, record: Material) => (
            <RevxMeasure
            value={value}
            metric={record.widthMm}
            type={MeasureType.LENGTH}
            withSymbol={false}
          />
          ),
        align: 'center',
        },
        {
          title: t('common.length'),
          dataIndex: 'lengthIn',
          render: (value: number, record: Material) => (
            <RevxMeasure
            value={value}
            metric={record.lengthMm}
            type={MeasureType.LENGTH}
            withSymbol={false}
          />
          ),
        align: 'center',
        },
      ],
    },
    {
      title: t('platform.mat.table.columns.category'),
      dataIndex: 'category',
    },
    {
      title: t('platform.mat.table.columns.color'),
      dataIndex: 'color',
      responsive: ['xxl'],
    },
    {
      title: t('platform.mat.table.columns.finish'),
      dataIndex: 'finish',
    },
    {
      title: basisWeightTitle(t('platform.mat.table.columns.basisWeight')),
      dataIndex: 'basisWeight',
      render: (value: string, record: Material) =>
        basisWeight(value, { metric: record.basisWeightMetric, withUnit: false }),
    },
    {
      title: columnTitle(
        t('platform.mat.table.columns.caliper'),
        MeasureType.CALIPER
      ),
      dataIndex: 'caliperIn',
      render: (value: number, record: Material) => (
        <RevxMeasure
            value={value}
            metric={record.caliperUm}
            type={MeasureType.CALIPER}
            withSymbol={false}
          />
      ),      responsive: ['xxl'],
    },
    {
      title: t('platform.mat.table.columns.costingUnit'),
      dataIndex: 'costUnitType',
      responsive: ['xxl'],
    },
    {
      title: t('platform.mat.table.columns.price'),
      dataIndex: 'priceUsd',
      valueType: 'money',
      responsive: ['xl'],
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'createAt',
      valueType: 'time',
      responsive: ['xxl'],
    },
    {
      title: t('common.active'),
      dataIndex: 'active',
      render: (active) => (
        <Tag color={active ? 'green' : 'volcano'}>
          {active ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (value, record) => (
        <RevxButton
          type="link"
          key="view"
          size="small"
          onClick={() => dispatch.mat.setState({ item: record })}
        >
          {t('common.view')}
        </RevxButton>
      ),
    },
  ];

  const { run: debounceSubmit, cancel: cancelDebounceSubmit } = useDebounceFn(
    () => {
      submit();
    },
    { wait: 1500 }
  );
  const submit = useMemoizedFn(async () => {
    await dispatch.org.saveOrg({ ...curOrg, defaultChooseMyPaper, defaultCollapseAdvancedSearch });
  });

  return (
    <div hidden={!!curItem}>
      <RevxFlexLayout>
        <div style={{ paddingRight: 12 }}>
          Set default to My Papers in job configuration:
        </div>
        <Radio.Group
          value={defaultChooseMyPaper}
          onChange={(e) => {
            setDefaultChooseMyPaper(e.target.value);
            cancelDebounceSubmit();
            debounceSubmit();
          }}
        >
          <Radio value={true}>Yes</Radio>
          <Radio value={false}>No</Radio>
        </Radio.Group>
        <div style={{ paddingLeft: 24, paddingRight: 12 }}>
          Advanced Search option collapsed by default
        </div>
        <Radio.Group
          value={defaultCollapseAdvancedSearch}
          onChange={(e) => {
            setDefaultCollapseAdvancedSearch(e.target.value);
            cancelDebounceSubmit();
            debounceSubmit();
          }}
        >
          <Radio value={true}>Yes</Radio>
          <Radio value={false}>No</Radio>
        </Radio.Group>
      </RevxFlexLayout>
      <RevxTable<Material>
        size="small"
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={{
          current: currentPage,
          pageSize,
          total,
        }}
        rowClassName={(record) => {
          if (curItem?.id === record.id) {
            return 'editing';
          }
          return '';
        }}
        onChange={onTableChange}
      />
    </div>
  );
};

export default MatAdminTable;
